"use client";

import { resolveFormClose } from "components/ui/v2/form-v2-props";
import {
  Button,
  DatePickerV2,
  ImageUploaderV2,
  type ImageUploaderV2File,
  Input,
  LiquidPill,
  ModalButtons,
  SwitcherV2,
  Textarea,
} from "components/ui/v2";
import { useLazyQuery, useMutation } from "@apollo/client";
import { ALLOWED_THUMBNAIL_EXTENSIONS } from "@constants/global";
import IaButton from "components/core/iaButton/IaButton";
import { Eye, Plus, Trash2 } from "lucide-react";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlCertificate, gqlFeedback } from "gql";
import { ICreateCertificateInput } from "gql/Certificates/mutations";
import { ICertificate } from "gql/Certificates/queries";
import useTranslation from "next-translate/useTranslation";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { getQueryOperator } from "utils/helpers";
import { putToSignedUrl } from "utils/storage-upload";
import { PreviewCertificateModalV2 } from "components/ui/v2/certificates/PreviewCertificateModalV2";

export const CERTIFICATE_FORM_ID = "certificate-form";

interface ICreateOrUpdateCertificateProps {
  certificate?: ICertificate | undefined;
  onClose?: () => void;
  hideHeader?: boolean;
  hideFooter?: boolean;
  formId?: string;
  onFooterChange?: (footer: ReactNode) => void;
}

export const CreateOrUpdateCertificate = (
  props: ICreateOrUpdateCertificateProps
) => {
  const {
    certificate,
    onClose: onCloseProp,
    hideHeader = false,
    hideFooter = false,
    formId = CERTIFICATE_FORM_ID,
    onFooterChange,
  } = props;
  const { t } = useTranslation("common");
  const close = resolveFormClose(onCloseProp, () => {});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [allowViewCourse, setAllowViewCourse] = useState(
    Boolean(certificate?.allowViewCourse)
  );
  const [customImage, setCustomImage] = useState<ImageUploaderV2File | null>(
    certificate?.customImage
      ? { preview: certificate.customImage, name: "certificate-badge" }
      : null
  );
  const MAX_LEARNING_OUTCOMES = 4;
  const { vendor } = useVendor();
  const [overrideVendorLogo, setOverrideVendorLogo] =
    useState<ImageUploaderV2File | null>({
      preview: certificate?.overrideVendorImage || vendor?.logo || "",
      name: "vendor-logo",
    });
  const { user } = useUserStoreContext((store) => ({
    user: store.user,
  }));
  const {
    handleSubmit: handleCreateCertificate,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ICreateCertificateInput>({
    defaultValues: {
      name: certificate?.name || "",
      subTitle: certificate?.subTitle || "",
      durationText: certificate?.durationText || "",
      startDate: certificate?.startDate
        ? new Date(certificate.startDate).toISOString()
        : "",
      certificateLabel: certificate?.certificateLabel || "",
      footerTextTitle: certificate?.footerTextTitle || "",
      allowViewCourse: certificate?.allowViewCourse || false,
      customImage: certificate?.customImage ? certificate.customImage : "",
      learningOutcomes: certificate?.learningOutcomes || [" "],
      courseUrl: certificate?.courseUrl || "",
      certificateTopLabel: certificate?.certificateTopLabel || "",
      titleTextMoreAboutCertificate: certificate?.titleTextMoreAboutCertificate,
      textMoreAboutCertificate: certificate?.textMoreAboutCertificate,
      buttonText: certificate?.buttonText || "",
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "learningOutcomes",
  });

  const handleAddLearningOutcome = () => {
    if (fields.length < MAX_LEARNING_OUTCOMES) {
      append(" ");
      setValue(`learningOutcomes.${fields.length}`, "");
    } else {
      toast.error("Solo se permiten hasta 4 aprendizajes esperados.");
    }
  };

  const handleRemoveLearningOutcome = (index: number) => {
    remove(index);
  };

  const [createCertificate, { loading: createCertificateLoading }] =
    useMutation<{ certificateInput: ICreateCertificateInput }>(
      gqlCertificate.mutations.CREATE_CERTIFICATE,
      {
        awaitRefetchQueries: true,
        refetchQueries: [
          getQueryOperator(gqlCertificate.queries.GET_CERTIFICATES),
        ],
        onCompleted() {
          toast.success(t("Certificado creado con exito"));
          close();
        },
        onError(e) {
          toast.error(e.message);
        },
      }
    );

  const [updateCertificate, { loading: updateCertificadoLoading }] =
    useMutation<{
      certificateInput: ICreateCertificateInput;
    }>(gqlCertificate.mutations.UPDATE_CERTIFICATE, {
      awaitRefetchQueries: true,
      refetchQueries: [
        getQueryOperator(gqlCertificate.queries.GET_CERTIFICATES),
      ],
      onCompleted() {
        toast.success(t("Certificado actualizado con cxito"));
        close();
      },
      onError(e) {
        toast.error(e.message);
      },
    });

  const [signedUrl] = useLazyQuery(gqlFeedback.queries.GENERATE_SIGNED_URL, {
    fetchPolicy: "network-only",
  });

  const uploadImage = async (image: { name: string; file: File }) => {
    const {
      data: { getSignedUrl: getSignedUrlMd },
    } = await signedUrl({
      variables: { key: image.name },
    });
    return putToSignedUrl(getSignedUrlMd, image.file).catch(() => null);
  };

  const handleUpdateOrCreate = async (data: ICreateCertificateInput) => {
    try {
      let thumbnailUrl = "";
      if (customImage?.file) {
        thumbnailUrl =
          (await uploadImage({
            name: customImage.name,
            file: customImage.file,
          })) || "";
      }

      let vendorLogoUrl = "";
      if (overrideVendorLogo?.file) {
        vendorLogoUrl =
          (await uploadImage({
            name: overrideVendorLogo.name,
            file: overrideVendorLogo.file,
          })) || "";
        setOverrideVendorLogo({
          preview: vendorLogoUrl,
          name: overrideVendorLogo.name,
        });
      }

      if (certificate) {
        return updateCertificate({
          variables: {
            updateCertificateInput: {
              id: certificate.id,
              ...data,
              customImage: customImage?.file
                ? thumbnailUrl
                : props.certificate?.customImage,
              allowViewCourse: allowViewCourse,
              overrideVendorImage: vendorLogoUrl
                ? vendorLogoUrl
                : overrideVendorLogo?.preview || "",
            },
          },
        });
      }

      createCertificate({
        variables: {
          createCertificateInput: {
            ...data,
            customImage: customImage?.file ? thumbnailUrl : "",
            allowViewCourse: allowViewCourse,
            overrideVendorImage: vendorLogoUrl
              ? vendorLogoUrl
              : overrideVendorLogo?.preview || "",
          },
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!certificate) {
      setValue("learningOutcomes.0", "");
    } else {
      setAllowViewCourse(Boolean(certificate?.allowViewCourse));
      setCustomImage(
        certificate?.customImage
          ? { preview: certificate.customImage, name: "certificate-badge" }
          : null
      );
    }
  }, [certificate, setValue]);

  const isSaving = createCertificateLoading || updateCertificadoLoading;
  const onFooterChangeRef = useRef(onFooterChange);
  onFooterChangeRef.current = onFooterChange;
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!hideFooter) return;
    const publish = onFooterChangeRef.current;
    if (!publish) return;

    publish(
      <div className="flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <LiquidPill
          as="button"
          type="button"
          variant="slate"
          appearance="tinted"
          surfaceStyle="selectionSolid"
          size="md"
          icon={<Eye className="h-4 w-4" />}
          text={t("Vista previa")}
          aria-label={t("Vista previa")}
          onClick={() => setPreviewOpen(true)}
        />
        <ModalButtons
          compact
          loading={isSaving}
          onCancel={() => closeRef.current()}
          submitLabel={certificate?.id ? t("Actualizar") : t("Guardar")}
          submitButtonProps={{ form: formId }}
        />
      </div>
    );

    return () => publish(null);
  }, [certificate?.id, formId, hideFooter, isSaving, t]);

  return (
    <div>
      {!hideHeader ? (
        <p className="mb-3 text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50">
          {certificate?.id ? t("Actualizar Certificado") : t("Agregar Certificado")}
        </p>
      ) : null}

      <form
        id={formId}
        onSubmit={handleCreateCertificate(handleUpdateOrCreate)}
      >
        <div className="mb-2 flex flex-wrap">
          <div className="w-full px-2 sm:w-1/2">
            <div className="mb-3">
              <Controller
                name="name"
                control={control}
                rules={{ required: t("Este campo es obligatorio") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="sm"
                    label={t("Nombre del certificado")}
                    error={errors.name?.message}
                    placeholder={t("Agregar nombre del certificado")}
                    maxLength={280}
                  />
                )}
              />
            </div>
            <div className="mb-3">
              <Controller
                name="subTitle"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="sm"
                    label={t("Subitulo del certificado")}
                    placeholder={t("Agregar subtitulo del certificado")}
                    maxLength={280}
                  />
                )}
              />
            </div>
          </div>
          <div className="w-full px-2 sm:w-1/2">
            <Controller
              name="customImage"
              control={control}
              render={({ field }) => (
                <ImageUploaderV2
                  value={customImage}
                  onChange={(next) => {
                    setCustomImage(next);
                    field.onChange(next?.preview ?? "");
                  }}
                  accept={ALLOWED_THUMBNAIL_EXTENSIONS}
                  size="md"
                  label={t("Distintivo de certificación")}
                  helperText={t("En caso que exista una insignia especial")}
                  emptyHint={t("Subir imagen")}
                  changeLabel={t("Cambiar")}
                  aria-label={t("Distintivo de certificación")}
                />
              )}
            />
          </div>
        </div>

        <div className="mb-2 flex flex-wrap">
          <div className="w-full px-2 sm:w-1/2">
            <Controller
              name="startDate"
              control={control}
              rules={{ required: t("Este campo es obligatorio") }}
              render={({ field }) => (
                <DatePickerV2
                  {...field}
                  value={field.value ? new Date(field.value) : undefined}
                  disabled={isSaving}
                  label={t("Fecha Inicio")}
                  placeholder={t("Fecha Inicio")}
                  error={errors.startDate?.message}
                  placement="auto"
                />
              )}
            />
          </div>
          <div className="w-full px-2 sm:w-1/2">
            <ImageUploaderV2
              value={
                overrideVendorLogo?.preview
                  ? overrideVendorLogo
                  : null
              }
              onChange={(next) => {
                setOverrideVendorLogo(
                  next ?? {
                    preview: vendor?.logo || "",
                    name: "vendor-logo",
                  }
                );
              }}
              accept={ALLOWED_THUMBNAIL_EXTENSIONS}
              size="md"
              label={t("Logo de la empresa en el certificado")}
              helperText={t("La imagen debe ser 320x120px")}
              emptyHint={t("Subir imagen")}
              changeLabel={t("Cambiar")}
              aria-label={t("Logo de la empresa en el certificado")}
            />
          </div>
        </div>

        <div className="mb-2 flex flex-wrap">
          <div className="w-full px-2 sm:w-1/2">
            <Controller
              name="certificateLabel"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  size="sm"
                  label={t("Etiqueta del certificado")}
                  disabled={isSaving}
                  placeholder={t("Ingrese la etiqueta del certificado")}
                  maxLength={280}
                />
              )}
            />
          </div>
          <div className="w-full px-2 sm:w-1/2">
            <Controller
              name="durationText"
              control={control}
              rules={{ required: t("Este campo es obligatorio") }}
              render={({ field }) => (
                <Input
                  {...field}
                  size="sm"
                  label={t("Duracion")}
                  error={errors.durationText?.message}
                  placeholder={t("CERTFICIATE_DURATION_PLACE_HOLDER")}
                  maxLength={280}
                />
              )}
            />
          </div>
        </div>

        <div className="mb-2 flex flex-wrap">
          <div className="w-full px-2">
            <Controller
              name="footerTextTitle"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  size="sm"
                  label={t("Descripción del curso y reconocimiento de certificados")}
                  disabled={isSaving}
                  placeholder={t("Texto pie de certificado")}
                  maxLength={280}
                  rows={5}
                  topEnd={
                    <IaButton
                      textToTransform={watch("footerTextTitle")}
                      setTextToTransform={(value) =>
                        setValue("footerTextTitle", value)
                      }
                      type="textarea"
                    />
                  }
                />
              )}
            />
          </div>
        </div>

        <div className="mb-2 flex flex-wrap">
          <div className="w-full px-2">
            <Controller
              name="certificateTopLabel"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  size="sm"
                  label={t("Titulo superior del certificado")}
                  placeholder={t(
                    "Agregar titulo que describe el tipo de certificación otorgada"
                  )}
                  maxLength={280}
                />
              )}
            />
          </div>
        </div>

        <div className="flex flex-wrap px-2">
          <div className="w-full">
            <p className="mt-3 text-[13px] font-medium leading-snug text-slate-900 dark:text-zinc-50">
              {t("Aprendizajes esperados")}
            </p>
            {fields.map((item, index) => (
              <div
                key={item.id}
                className="mb-2 flex items-center gap-2 align-middle"
              >
                <Controller
                  name={`learningOutcomes.${index}`}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      size="sm"
                      error={
                        fieldState.error
                          ? t("Este campo es obligatorio")
                          : undefined
                      }
                      placeholder={t("Agregar aprendizaje esperado del curso")}
                      maxLength={280}
                    />
                  )}
                />
                <LiquidPill
                  as="button"
                  type="button"
                  variant="rose"
                  appearance="tinted"
                  shape="circle"
                  size="sm"
                  icon={<Trash2 className="h-4 w-4" />}
                  aria-label={t("Eliminar")}
                  onClick={() => handleRemoveLearningOutcome(index)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center px-2 py-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleAddLearningOutcome}
            size="sm"
          >
            <span>{t("Agregar aprendizaje")}</span>
            <Plus className="ml-2 h-4 w-4" />
          </Button>
        </div>

        <div className="mb-2 mt-2 flex w-full items-center gap-2 px-2 sm:w-1/2">
          <Controller
            name="allowViewCourse"
            control={control}
            render={({ field }) => (
              <SwitcherV2
                dense
                label={t("Permitir ver el curso")}
                checked={Boolean(field.value)}
                onCheckedChange={(value) => {
                  field.onChange(value);
                  setAllowViewCourse(value);
                }}
              />
            )}
          />
        </div>

        {allowViewCourse ? (
          <div className="mb-2 flex w-full flex-col">
            <div className="mb-2 w-full px-2">
              <Controller
                name="courseUrl"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="sm"
                    label={t("URL del curso")}
                    placeholder={t("Ingrese la URL del curso")}
                    maxLength={280}
                  />
                )}
              />
            </div>
            <div className="mb-2 w-full px-2">
              <Controller
                name="buttonText"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="sm"
                    label={t("Texto en boton")}
                    placeholder={t(
                      "Ingrese el texto que desea que aparezca en boton del certificado"
                    )}
                    maxLength={280}
                  />
                )}
              />
            </div>
            <div className="mb-2 w-full px-2">
              <Controller
                name="titleTextMoreAboutCertificate"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="sm"
                    label={t("Título de la invitación a visualizar el curso")}
                    placeholder={t(
                      "Ingrese un titulo que describa una invitación a ver el curso"
                    )}
                    maxLength={280}
                  />
                )}
              />
            </div>
            <div className="mb-2 w-full px-2">
              <Controller
                name="textMoreAboutCertificate"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    size="sm"
                    label={t("Texto de invitación a visualizar el curso")}
                    disabled={createCertificateLoading}
                    placeholder={t(
                      "Crea un texto para invitar a los usuarios a visualizar el contenido del curso"
                    )}
                    maxLength={280}
                    rows={5}
                    topEnd={
                      <IaButton
                        textToTransform={watch("textMoreAboutCertificate")}
                        setTextToTransform={(value) =>
                          setValue("textMoreAboutCertificate", value)
                        }
                        type="textarea"
                      />
                    }
                  />
                )}
              />
            </div>
          </div>
        ) : null}

        {!hideFooter ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 px-2">
            <LiquidPill
              as="button"
              type="button"
              variant="slate"
              appearance="tinted"
              surfaceStyle="selectionSolid"
              size="md"
              icon={<Eye className="h-4 w-4" />}
              text={t("Vista previa")}
              aria-label={t("Vista previa")}
              onClick={() => setPreviewOpen(true)}
            />
            <ModalButtons
              compact
              onCancel={close}
              loading={isSaving}
              submitLabel={certificate?.id ? t("Actualizar") : t("Guardar")}
            />
          </div>
        ) : null}
      </form>

      <PreviewCertificateModalV2
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        certificate={{
          name: getValues("name"),
          duration: getValues("durationText"),
          subtitle: getValues("subTitle"),
          badge: customImage?.preview,
          certificateLabel: getValues("certificateLabel"),
          vendorImage: overrideVendorLogo?.preview,
          userName: user?.name,
          userLastName: user?.lastname,
          certificateTopLabel: getValues("certificateTopLabel"),
          preview: true,
        }}
      />
    </div>
  );
};
